import { useState, useEffect, useMemo } from 'react';
import { useCustomMutation, useCustom, useApiUrl } from '@refinedev/core';
import { useAllPages } from '@/hooks/use-all-pages';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  User,
  Phone,
  AtSign,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowLeft,
  UserPlus,
  ExternalLink,
  X,
  Send,
  Tag as TagIcon,
  Check,
  Globe,
  Bot as BotIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { BotSelect } from '@/components/shared/BotSelect';
import { UserSearchCombobox } from '@/components/shared';
import type { User as UserType } from '@/resources/users/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn, startOfDayUtcIso } from '@/lib/utils';
import { DatePicker } from '@/components/ui/date-picker';
import { notify } from '@/lib/notify';
import type { TelegramUserSearchResult, TelegramSessionOption } from './types';
import type { MessageTemplate } from '@/resources/message-templates';
import { InvitationItemsBuilder, type InvitationItem } from './InvitationItemsBuilder';

interface CreateInvitationDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  hasSession: boolean;
  templates: MessageTemplate[];
  templatesLoading: boolean;
}

interface ErrorWithMessage {
  message: string;
}

function isErrorWithMessage(value: unknown): value is ErrorWithMessage {
  return (
    typeof value === 'object' &&
    value !== null &&
    'message' in value &&
    typeof value.message === 'string' &&
    value.message.length > 0
  );
}

function getErrorMessage(error: unknown, fallback: string): string {
  return isErrorWithMessage(error) ? error.message : fallback;
}

interface TagOption {
  id: number;
  name: string;
  color: string;
  deletedAt: string | null;
}

interface CreatedInvitation {
  id: number;
}

function isCreatedInvitation(value: unknown): value is CreatedInvitation {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    typeof value.id === 'number'
  );
}

function getCreatedInvitationId(data: unknown): number | null {
  return isCreatedInvitation(data) ? data.id : null;
}

/**
 * Модалка создания приглашения: поиск пользователя в Telegram,
 * опциональная привязка абонемента и немедленная отправка одним действием.
 */
export const CreateInvitationDialog = ({
  open,
  onClose,
  onCreated,
  hasSession,
  templates,
  templatesLoading,
}: CreateInvitationDialogProps) => {
  const navigate = useNavigate();
  const apiUrl = useApiUrl();
  const [channel, setChannel] = useState<'TELEGRAM' | 'EMAIL'>('TELEGRAM');
  const [emailValue, setEmailValue] = useState('');
  const [searchType, setSearchType] = useState<'username' | 'phone'>('username');
  const [searchValue, setSearchValue] = useState('');
  const [searchResult, setSearchResult] = useState<TelegramUserSearchResult | null>(null);
  const [items, setItems] = useState<InvitationItem[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('default');
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  // Бот, через которого уйдёт приглашение и сформируется t.me/<bot>?start=invite_…
  // null = первый активный (раньше — isPrimary).
  const [selectedBotId, setSelectedBotId] = useState<number | null>(null);
  // EMAIL-канал: опциональная привязка к Telegram-аккаунту получателя.
  // Если задана — бот при /start от этого юзера найдёт инвайт по telegramUserId
  // и примет автоматически (даже если письмо не открывалось).
  const [emailTelegramUser, setEmailTelegramUser] = useState<UserType | null>(null);
  // Куда ведёт ссылка в отправляемом приглашении:
  //  - TELEGRAM_BOT — t.me/<bot>?start=invite_<token> (чат с ботом, /start принимает)
  //  - PLATFORM — {PLATFORM_BASE_URL}/invite/<token> (страница платформы)
  // Применяется и к TELEGRAM-каналу (userbot), и к EMAIL-каналу (письмо).
  const [linkTarget, setLinkTarget] = useState<'TELEGRAM_BOT' | 'PLATFORM'>('TELEGRAM_BOT');
  // Опциональная дата окончания выдаваемого доступа (YYYY-MM-DD). Пусто = бессрочно/по тарифу.
  const [accessEndsAt, setAccessEndsAt] = useState<Date | null>(null);
  // Обязательно ли найти пользователя в Telegram перед созданием приглашения.
  // Снятая галочка разрешает создать нерезолвленное приглашение по телефону/
  // username, если resolveUsername/resolvePhone не сработали (скрытый номер,
  // приватный профиль) — матчинг произойдёт при регистрации пользователя.
  const [requireResolved, setRequireResolved] = useState(true);
  // Co-invite: второй член семьи, если среди items выбран семейный тариф.
  const [familyMemberContact, setFamilyMemberContact] = useState('');

  // Определяем, выбран ли среди items семейный тариф — тянем последний
  // SUBSCRIPTION_PLAN item с заполненным pricingTierId и проверяем isFamily.
  const selectedPlanTierId = useMemo(() => {
    const planItems = items.filter((i) => i.targetType === 'SUBSCRIPTION_PLAN' && i.pricingTierId !== null);
    return planItems.length > 0 ? planItems[planItems.length - 1].pricingTierId : null;
  }, [items]);

  const { query: selectedTierQuery } = useCustom<{ isFamily: boolean; familySlots: number }>({
    url: `${apiUrl}/pricing-tiers/admin/${selectedPlanTierId}`,
    method: 'get',
    queryOptions: { enabled: selectedPlanTierId !== null },
  });
  const isFamilyTierSelected = selectedTierQuery.data?.data?.isFamily === true;

  // Мутация поиска
  const searchMutation = useCustomMutation<TelegramUserSearchResult>();
  const searchUser = searchMutation.mutate;
  const isSearching = searchMutation.mutation.isPending;

  // Загрузка тегов — для multi-select выдачи после accept.
  // Парционно через useAllPages, чтобы не упереться в backend-лимит 500.
  const { data: allTags } = useAllPages<TagOption>({
    resource: 'tags',
    enabled: open,
    staleTime: 30_000,
  });
  const activeTags = useMemo<TagOption[]>(
    () => allTags.filter((t) => t.deletedAt === null),
    [allTags],
  );
  const tagsById = useMemo(() => {
    const m = new Map<number, TagOption>();
    for (const t of activeTags) { m.set(t.id, t); }
    return m;
  }, [activeTags]);

  const toggleTag = (id: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // Доступные Telegram-сессии (свои + расшаренные). Только AUTHORIZED.
  const { query: sessionsQuery } = useCustom<TelegramSessionOption[]>({
    url: `${apiUrl}/telegram-client/accounts`,
    method: 'get',
    queryOptions: {
      enabled: open && channel === 'TELEGRAM',
    },
  });

  const telegramSessions: TelegramSessionOption[] = (() => {
    const raw = sessionsQuery.data?.data;
    if (Array.isArray(raw)) {
      return raw.filter((s) => s.status === 'AUTHORIZED');
    }
    return [];
  })();

  // Дефолт — активная сессия, иначе первая доступная
  useEffect(() => {
    if (channel !== 'TELEGRAM' || telegramSessions.length === 0) {
      return;
    }
    const stillValid = telegramSessions.some((s) => s.id === selectedSessionId);
    if (!stillValid) {
      const active = telegramSessions.find((s) => s.isActive);
      setSelectedSessionId(active ? active.id : telegramSessions[0].id);
    }
  }, [channel, telegramSessions, selectedSessionId]);

  const formatSessionLabel = (s: TelegramSessionOption): string => {
    const name =
      [s.telegramFirstName, s.telegramLastName].filter(Boolean).join(' ') ||
      (s.telegramUsername ? `@${s.telegramUsername}` : s.telegramPhone) ||
      'Telegram аккаунт';
    if (s.isShared && s.ownerName) {
      return `${name} (от @${s.ownerName})`;
    }
    return name;
  };

  /**
   * Item невалиден если:
   *  - targetId не выбран;
   *  - SUBSCRIPTION_PLAN с обязательным тарифом — pricingTierId не выбран
   *    (проверка наличия тарифов делается в InvitationItemsBuilder; здесь —
   *    общая heuristic: для SUBSCRIPTION_PLAN тариф желателен).
   * Если items пуст — приглашение всё равно валидно (см. ТЗ: «пригласить
   * без абонемента» исторически разрешено). Сервис примет.
   */
  const itemsInvalid = items.some((i) => {
    if (i.targetId === null) { return true; }
    // Для SUBSCRIPTION_PLAN — если в строке загружены тарифы (через builder),
    // тариф обязателен. На уровне диалога это знание недоступно, поэтому
    // дополнительная проверка делается сервером и InvitationItemsBuilder
    // подсветит ошибку под селектом.
    return false;
  });

  // Мутация создания приглашения
  const createMutation = useCustomMutation();
  const createInvitation = createMutation.mutate;

  // Мутация отправки приглашения
  const sendMutation = useCustomMutation();
  const sendInvitation = sendMutation.mutate;

  const isSubmitting = createMutation.mutation.isPending || sendMutation.mutation.isPending;

  const resetState = () => {
    setChannel('TELEGRAM');
    setEmailValue('');
    setSearchType('username');
    setSearchValue('');
    setSearchResult(null);
    setItems([]);
    setSelectedTemplate('default');
    setSelectedSessionId(null);
    setSelectedTagIds([]);
    setTagPickerOpen(false);
    setSelectedBotId(null);
    setLinkTarget('TELEGRAM_BOT');
    setEmailTelegramUser(null);
    setAccessEndsAt(null);
    setRequireResolved(true);
    setFamilyMemberContact('');
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    resetState();
    onClose();
  };

  const handleSearch = () => {
    if (!searchValue.trim()) {
      notify.error('Введите значение для поиска');
      return;
    }

    setSearchResult(null);
    searchUser(
      {
        url: '/user-invitations/search',
        method: 'post',
        values: {
          type: searchType,
          value: searchValue.trim(),
          ...(selectedSessionId !== null ? { sessionId: selectedSessionId } : {}),
        },
      },
      {
        onSuccess: (response) => {
          setSearchResult(response.data);
          if (!response.data.found) {
            notify.error('Пользователь не найден в Telegram');
          }
        },
        onError: (error) => {
          notify.error(getErrorMessage(error, 'Ошибка поиска'));
        },
      }
    );
  };

  /**
   * Собирает co-invite поля из familyMemberContact для payload создания.
   * Пусто/не семейный тариф → {} (поля не отправляются). Формат контакта
   * определяется так же, как в bulk-импорте: @username или +phone.
   */
  const buildFamilyMemberPayload = (): {
    familyMemberTelegramPhone?: string;
    familyMemberTelegramUsername?: string;
  } => {
    const contact = familyMemberContact.trim();
    if (!isFamilyTierSelected || !contact) {
      return {};
    }
    const isPhone = /^\+?\d{6,}$/.test(contact);
    return isPhone
      ? { familyMemberTelegramPhone: contact }
      : { familyMemberTelegramUsername: contact.replace(/^@/, '') };
  };

  const handleCreateAndSend = () => {
    if (!searchResult?.user) {
      return;
    }

    if (itemsInvalid) {
      notify.error('Заполните все строки доступа или удалите пустые');
      return;
    }

    const user = searchResult.user;
    createInvitation(
      {
        url: '/user-invitations',
        method: 'post',
        values: {
          telegramUserId: user.telegramId,
          telegramUsername: user.username,
          telegramFirstName: user.firstName,
          telegramLastName: user.lastName,
          telegramPhone: user.phone,
          avatarBase64: user.photoBase64,
          tagIds: selectedTagIds.length > 0 ? selectedTagIds : null,
          botId: selectedBotId,
          items: items.length > 0 ? items : null,
          accessEndsAt: accessEndsAt ? startOfDayUtcIso(accessEndsAt) : null,
          ...buildFamilyMemberPayload(),
        },
      },
      {
        onSuccess: (createResponse) => {
          const invitationId = getCreatedInvitationId(createResponse.data);
          if (invitationId === null) {
            notify.error('Приглашение создано, но не удалось получить его ID для отправки');
            resetState();
            onCreated();
            onClose();
            return;
          }

          const templateId =
            selectedTemplate !== 'default' ? parseInt(selectedTemplate, 10) : undefined;

          sendInvitation(
            {
              url: `/user-invitations/${invitationId}/send`,
              method: 'post',
              values: {
                ...(templateId !== undefined ? { templateId } : {}),
                ...(selectedSessionId !== null ? { sessionId: selectedSessionId } : {}),
                telegramLinkTarget: linkTarget,
              },
            },
            {
              onSuccess: () => {
                notify.success('Приглашение создано и отправлено');
                resetState();
                onCreated();
                onClose();
              },
              onError: (error) => {
                notify.error(
                  `Приглашение создано, но не отправлено: ${getErrorMessage(error, 'ошибка отправки')}`
                );
                resetState();
                onCreated();
                onClose();
              },
            }
          );
        },
        onError: (error) => {
          notify.error(getErrorMessage(error, 'Ошибка создания'));
        },
      }
    );
  };

  /**
   * Создаёт нерезолвленное TELEGRAM-приглашение (требуется чекбокс "не обязательно
   * найти в Telegram"): пользователь не найден через resolveUsername/resolvePhone
   * (скрытый номер/приватный профиль), но контакт сохраняется для отложенного
   * матчинга при регистрации. Не отправляется сразу — деликат-доставки для
   * нерезолвленного контакта не существует.
   */
  const handleCreateUnresolved = () => {
    if (!searchValue.trim()) {
      return;
    }
    if (itemsInvalid) {
      notify.error('Заполните все строки доступа или удалите пустые');
      return;
    }

    const contact = searchValue.trim();
    createInvitation(
      {
        url: '/user-invitations',
        method: 'post',
        values: {
          requireResolved: false,
          ...(searchType === 'phone'
            ? { telegramPhone: contact }
            : { telegramUsername: contact.replace(/^@/, '') }),
          tagIds: selectedTagIds.length > 0 ? selectedTagIds : null,
          botId: selectedBotId,
          items: items.length > 0 ? items : null,
          accessEndsAt: accessEndsAt ? startOfDayUtcIso(accessEndsAt) : null,
          ...buildFamilyMemberPayload(),
        },
      },
      {
        onSuccess: () => {
          notify.success(
            'Приглашение создано без резолва. Доступ выдастся автоматически при регистрации пользователя.',
          );
          resetState();
          onCreated();
          onClose();
        },
        onError: (error) => {
          notify.error(getErrorMessage(error, 'Ошибка создания'));
        },
      },
    );
  };

  const handleCreateAndSendEmail = () => {
    const email = emailValue.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      notify.error('Введите корректный email');
      return;
    }

    if (itemsInvalid) {
      notify.error('Заполните все строки доступа или удалите пустые');
      return;
    }

    createInvitation(
      {
        url: '/user-invitations',
        method: 'post',
        values: {
          channel: 'EMAIL',
          email,
          tagIds: selectedTagIds.length > 0 ? selectedTagIds : null,
          botId: selectedBotId,
          items: items.length > 0 ? items : null,
          accessEndsAt: accessEndsAt ? startOfDayUtcIso(accessEndsAt) : null,
          ...buildFamilyMemberPayload(),
          // Опциональная привязка к Telegram-аккаунту.
          ...(emailTelegramUser
            ? {
                telegramUserId: Number(emailTelegramUser.telegramId),
                telegramUsername: emailTelegramUser.username ?? null,
                telegramFirstName: emailTelegramUser.firstName ?? null,
                telegramLastName: emailTelegramUser.lastName ?? null,
                telegramPhone: emailTelegramUser.phone ?? null,
              }
            : {}),
        },
      },
      {
        onSuccess: (createResponse) => {
          const invitationId = getCreatedInvitationId(createResponse.data);
          if (invitationId === null) {
            notify.error('Приглашение создано, но не удалось получить его ID для отправки');
            resetState();
            onCreated();
            onClose();
            return;
          }

          sendInvitation(
            {
              url: `/user-invitations/${invitationId}/send`,
              method: 'post',
              values: {
                emailLinkTarget: linkTarget,
                ...(linkTarget === 'TELEGRAM_BOT' ? { emailLinkBotId: selectedBotId } : {}),
              },
            },
            {
              onSuccess: () => {
                notify.success(`Приглашение отправлено на ${email}`);
                resetState();
                onCreated();
                onClose();
              },
              onError: (error) => {
                notify.error(
                  `Приглашение создано, но не отправлено: ${getErrorMessage(error, 'ошибка отправки')}`
                );
                resetState();
                onCreated();
                onClose();
              },
            }
          );
        },
        onError: (error) => {
          notify.error(getErrorMessage(error, 'Ошибка создания'));
        },
      }
    );
  };

  const handleReset = () => {
    setSearchValue('');
    setSearchResult(null);
    setItems([]);
  };

  const user = searchResult?.user;

  const linkTargetBlock = (
    <div className="rounded-lg border p-4 space-y-3">
      <Label className="text-sm font-medium">Куда ведёт ссылка</Label>
      <Tabs value={linkTarget} onValueChange={(v) => setLinkTarget(v as 'TELEGRAM_BOT' | 'PLATFORM')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="TELEGRAM_BOT" disabled={isSubmitting} className="gap-2">
            <BotIcon className="h-4 w-4" />
            Telegram-бот
          </TabsTrigger>
          <TabsTrigger value="PLATFORM" disabled={isSubmitting} className="gap-2">
            <Globe className="h-4 w-4" />
            Платформа
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <p className="text-xs text-muted-foreground">
        {linkTarget === 'TELEGRAM_BOT'
          ? 'Ссылка вида t.me/<bot>?start=invite_… — открывает чат с ботом, после нажатия Start приглашение принимается автоматически.'
          : 'Ссылка на platform.mastersclub.uz/invite/… — открывает страницу платформы (приглашение примется после авторизации).'}
      </p>
    </div>
  );

  const botSelectBlock = (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-sm font-medium">Бот для приглашения</Label>
      </div>
      <p className="text-xs text-muted-foreground">
        Через какого Telegram-бота отправлять приглашение и формировать ссылку
        t.me/&lt;bot&gt;?start=invite_…. «По умолчанию» = первый активный бот.
      </p>
      <BotSelect value={selectedBotId} onChange={setSelectedBotId} disabled={isSubmitting} />
    </div>
  );

  const tagsPickerBlock = (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="flex items-center gap-2">
        <TagIcon className="h-4 w-4 text-muted-foreground" />
        <Label className="text-sm font-medium">Теги после регистрации</Label>
      </div>
      <p className="text-xs text-muted-foreground">
        Выбранные теги автоматически будут назначены пользователю сразу после принятия приглашения.
      </p>
      <Popover open={tagPickerOpen} onOpenChange={setTagPickerOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" type="button" className="w-full justify-between font-normal">
            <span className="flex flex-wrap items-center gap-1 min-w-0">
              {selectedTagIds.length === 0 ? (
                <span className="text-muted-foreground">Выберите теги (опционально)</span>
              ) : (
                selectedTagIds.map((id) => {
                  const t = tagsById.get(id);
                  if (!t) { return null; }
                  return (
                    <Badge
                      key={id}
                      variant="secondary"
                      className="gap-1"
                      style={{ backgroundColor: `${t.color}20`, color: t.color, borderColor: t.color }}
                    >
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: t.color }} />
                      {t.name}
                    </Badge>
                  );
                })
              )}
            </span>
            <TagIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" sideOffset={4}>
          <Command>
            <CommandInput placeholder="Поиск тега..." />
            <CommandList>
              <CommandEmpty>Теги не найдены</CommandEmpty>
              <CommandGroup>
                {activeTags.map((tag) => {
                  const checked = selectedTagIds.includes(tag.id);
                  return (
                    <CommandItem
                      key={tag.id}
                      value={`${tag.name}-${tag.id}`}
                      onSelect={() => toggleTag(tag.id)}
                    >
                      <Check className={cn('mr-2 h-4 w-4', checked ? 'opacity-100' : 'opacity-0')} />
                      <span
                        className="h-3 w-3 rounded-full mr-2 flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );

  const accessEndsBlock = (
    <div className="rounded-lg border p-4 space-y-3">
      <Label className="text-sm font-medium">
        Доступ до даты (опционально)
      </Label>
      <p className="text-xs text-muted-foreground">
        Если задано — выдаваемый доступ закончится в указанную дату, независимо от
        срока тарифа. Пусто = бессрочно или по сроку тарифа.
      </p>
      <DatePicker
        value={accessEndsAt}
        onChange={setAccessEndsAt}
        disabled={isSubmitting}
        className="w-fit"
        placeholder="По сроку тарифа"
      />
    </div>
  );

  const familyMemberBlock = isFamilyTierSelected ? (
    <div className="rounded-lg border p-4 space-y-3">
      <Label className="text-sm font-medium">
        Второй член семьи (co-invite)
      </Label>
      <p className="text-xs text-muted-foreground">
        Выбран семейный тариф — можно сразу указать второго члена семьи (@username
        или телефон). Если он уже зарегистрирован — доступ выдастся автоматически
        сразу после принятия приглашения владельцем; если нет — при его регистрации.
      </p>
      <Input
        value={familyMemberContact}
        onChange={(e) => setFamilyMemberContact(e.target.value)}
        placeholder="@username или +998901234567"
        disabled={isSubmitting}
      />
    </div>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { handleClose(); } }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        // После успешного поиска форма (с кнопкой «Найти», на которой фокус)
        // размонтируется → фокус уходит в body, Radix трактует это как
        // focus-outside и закрывает диалог. Гасим авто-закрытие по потере
        // фокуса; намеренное закрытие (Esc, крестик, клик снаружи) работает.
        onFocusOutside={(e) => { e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Добавить пользователя
          </DialogTitle>
          <DialogDescription>
            Приглашение через Telegram (поиск пользователя) или на email со ссылкой на платформу
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Переключатель канала доставки */}
          <div className="space-y-3">
            <Label>Канал приглашения</Label>
            <RadioGroup
              value={channel}
              onValueChange={(v) => setChannel(v as 'TELEGRAM' | 'EMAIL')}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="TELEGRAM" id="channel-telegram" />
                <Label htmlFor="channel-telegram" className="flex items-center gap-2 cursor-pointer">
                  <Send className="h-4 w-4" />
                  Telegram
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="EMAIL" id="channel-email" />
                <Label htmlFor="channel-email" className="flex items-center gap-2 cursor-pointer">
                  <AtSign className="h-4 w-4" />
                  Email
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* ===== EMAIL-канал ===== */}
          {channel === 'EMAIL' && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email пользователя</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="user@example.com"
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateAndSendEmail();
                    }
                  }}
                />
                <p className="text-sm text-muted-foreground">
                  На указанный email придёт письмо со ссылкой на платформу. Приглашение
                  примется автоматически после входа (email, Telegram или Google).
                </p>
              </div>

              <InvitationItemsBuilder value={items} onChange={setItems} enabled={open} />

              {familyMemberBlock}

              {tagsPickerBlock}

              {accessEndsBlock}

              <div className="rounded-lg border p-4 space-y-3">
                <Label className="text-sm font-medium">
                  Telegram-аккаунт получателя (опционально)
                </Label>
                <p className="text-xs text-muted-foreground">
                  Если знаете Telegram-аккаунт получателя — привяжите его. Тогда при
                  /start от этого пользователя в боте приглашение примется автоматически,
                  даже если письмо не открывалось.
                </p>
                <UserSearchCombobox
                  value={emailTelegramUser}
                  onChange={setEmailTelegramUser}
                  placeholder="Выберите пользователя с Telegram"
                  disabled={isSubmitting}
                  requireTelegramId
                />
              </div>
            </div>
          )}

          {/* Выбор куда ведёт ссылка в письме (платформа или Telegram-бот). */}
          {channel === 'EMAIL' && linkTargetBlock}

          {/* Бот нужен только если ссылка ведёт в Telegram-бота (startapp-deeplink). */}
          {channel === 'EMAIL' && linkTarget === 'TELEGRAM_BOT' && botSelectBlock}

          {/* ===== TELEGRAM-канал ===== */}
          {channel === 'TELEGRAM' && (
          <>
          {/* Предупреждение об отсутствии сессии */}
          {!hasSession && (
            <div className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800 dark:text-yellow-200">
                  Telegram сессия не авторизована
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  Для поиска пользователей необходимо авторизовать Telegram сессию.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    handleClose();
                    navigate('/telegram-client');
                  }}
                >
                  Перейти к авторизации
                </Button>
              </div>
            </div>
          )}

          {/* Выбор Telegram-сессии для поиска и отправки */}
          {hasSession && telegramSessions.length > 0 && (
            <div className="space-y-2">
              <Label>Telegram-аккаунт для отправки</Label>
              <Select
                value={selectedSessionId !== null ? String(selectedSessionId) : undefined}
                onValueChange={(v) => setSelectedSessionId(parseInt(v, 10))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Выберите аккаунт" />
                </SelectTrigger>
                <SelectContent>
                  {telegramSessions.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {formatSessionLabel(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Форма поиска */}
          {hasSession && !searchResult?.found && (
            <div className="space-y-6">
              <div className="space-y-3">
                <Label>Тип поиска</Label>
                <RadioGroup
                  value={searchType}
                  onValueChange={(v) => setSearchType(v as 'username' | 'phone')}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="username" id="search-username" />
                    <Label htmlFor="search-username" className="flex items-center gap-2 cursor-pointer">
                      <AtSign className="h-4 w-4" />
                      Username
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="phone" id="search-phone" />
                    <Label htmlFor="search-phone" className="flex items-center gap-2 cursor-pointer">
                      <Phone className="h-4 w-4" />
                      Телефон
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="search-value">
                  {searchType === 'username' ? 'Username' : 'Номер телефона'}
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="search-value"
                    placeholder={searchType === 'username' ? '@username или username' : '+998901234567'}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                  />
                  <Button type="button" onClick={handleSearch} disabled={isSearching || !searchValue.trim()}>
                    {isSearching ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                    <span className="ml-2">Найти</span>
                  </Button>
                </div>
                {searchType === 'phone' && (
                  <p className="text-sm text-muted-foreground">
                    Поиск по телефону может не работать, если пользователь скрыл номер в настройках приватности.
                  </p>
                )}
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="require-resolved"
                  checked={requireResolved}
                  onCheckedChange={(v) => setRequireResolved(v === true)}
                />
                <div className="grid gap-1 leading-none">
                  <Label htmlFor="require-resolved" className="cursor-pointer font-normal">
                    Обязательно найти в Telegram
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Если снять — при неудачном поиске (скрытый номер, приватный профиль)
                    можно будет создать приглашение по телефону/username напрямую.
                    Доступ выдастся автоматически при регистрации этого пользователя.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Результат поиска - не найден */}
          {searchResult && !searchResult.found && (
            <div className="rounded-lg border border-destructive p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <X className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <p className="font-medium">Пользователь не найден</p>
                  <p className="text-sm text-muted-foreground">
                    {requireResolved
                      ? 'Проверьте правильность данных и попробуйте снова'
                      : 'Можно создать приглашение без резолва — оно примется автоматически при регистрации.'}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" onClick={handleReset}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Искать другого
                </Button>
                {!requireResolved && (
                  <Button onClick={handleCreateUnresolved} disabled={isSubmitting || itemsInvalid}>
                    {isSubmitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <UserPlus className="h-4 w-4 mr-2" />
                    )}
                    Создать без резолва
                  </Button>
                )}
              </div>

              {!requireResolved && (
                <div className="mt-6 space-y-6">
                  <InvitationItemsBuilder value={items} onChange={setItems} enabled={open} />
                  {familyMemberBlock}
                  {tagsPickerBlock}
                  {accessEndsBlock}
                </div>
              )}
            </div>
          )}

          {/* Результат поиска - найден */}
          {searchResult?.found && user && (
            <div className="space-y-6">
              {/* Информация о пользователе */}
              <div className="flex items-start gap-4">
                {user.photoBase64 ? (
                  <img
                    src={`data:image/jpeg;base64,${user.photoBase64}`}
                    alt=""
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
                    <User className="h-10 w-10 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <div>
                    <p className="text-lg font-medium">
                      {[user.firstName, user.lastName].filter(Boolean).join(' ') || 'Без имени'}
                    </p>
                    {user.username && (
                      <p className="text-muted-foreground flex items-center gap-1">
                        <AtSign className="h-4 w-4" />
                        {user.username}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 text-sm">
                    <Badge variant="outline">
                      ID: {user.telegramId}
                    </Badge>
                    {user.phone && (
                      <Badge variant="outline" className="gap-1">
                        <Phone className="h-3 w-3" />
                        {user.phone}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Статусы */}
              <div className="space-y-3">
                {user.alreadyInSystem && (
                  <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-blue-800 dark:text-blue-200">
                        Пользователь уже в системе
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                        Этот пользователь уже зарегистрирован на платформе.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          handleClose();
                          navigate(`/users/show/${user.existingUserId}`);
                        }}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Открыть профиль
                      </Button>
                    </div>
                  </div>
                )}

                {user.alreadyInvited && (
                  <div className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-yellow-800 dark:text-yellow-200">
                        Приглашение уже создано
                      </p>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                        Для этого пользователя уже существует приглашение.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          handleClose();
                          navigate(`/user-invitations/show/${user.existingInvitationId}`);
                        }}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Открыть приглашение
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Что выдать после регистрации (multi-items) */}
              {!user.alreadyInSystem && !user.alreadyInvited && (
                <InvitationItemsBuilder value={items} onChange={setItems} enabled={open} />
              )}

              {/* Co-invite: второй член семьи (только для семейного тарифа) */}
              {!user.alreadyInSystem && !user.alreadyInvited && familyMemberBlock}

              {/* Теги после регистрации (multi-select) */}
              {!user.alreadyInSystem && !user.alreadyInvited && tagsPickerBlock}

              {/* Доступ до даты (опционально) */}
              {!user.alreadyInSystem && !user.alreadyInvited && accessEndsBlock}

              {/* Куда ведёт ссылка в отправляемом сообщении: бот (?start=) или платформа */}
              {!user.alreadyInSystem && !user.alreadyInvited && linkTargetBlock}

              {/* Бот для отправки — ссылка t.me/<bot>?start=invite_… собирается из выбранного.
                  Если ссылка ведёт на платформу — бот для самого URL не нужен. */}
              {!user.alreadyInSystem && !user.alreadyInvited && linkTarget === 'TELEGRAM_BOT' && botSelectBlock}

              {/* Шаблон сообщения */}
              {!user.alreadyInSystem && !user.alreadyInvited && (
                <div className="space-y-2">
                  <Label>Шаблон сообщения</Label>
                  <Select
                    value={selectedTemplate}
                    onValueChange={setSelectedTemplate}
                    disabled={templatesLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Выберите шаблон" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">По умолчанию</SelectItem>
                      {templates.map((template) => (
                        <SelectItem key={template.id} value={String(template.id)}>
                          {template.name ?? template.slug ?? `#${template.id}`}
                          {template.isDefault && ' (по умолчанию)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Отмена
          </Button>
          {channel === 'TELEGRAM' && searchResult?.found && user && !user.alreadyInSystem && !user.alreadyInvited && (
            <>
              <Button variant="outline" onClick={handleReset} disabled={isSubmitting}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Искать другого
              </Button>
              <Button
                onClick={handleCreateAndSend}
                disabled={isSubmitting || itemsInvalid}
                title={itemsInvalid ? 'Заполните все строки доступа' : undefined}
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Создать и отправить
              </Button>
            </>
          )}
          {channel === 'EMAIL' && (
            <Button
              onClick={handleCreateAndSendEmail}
              disabled={isSubmitting || itemsInvalid || emailValue.trim() === ''}
              title={itemsInvalid ? 'Заполните все строки доступа' : undefined}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Создать и отправить
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
